import React, { ReactElement } from 'react';
import { Card } from 'antd';

import { Address, Airport, BlackoutDate, Breeder, Customer, State } from 'types';
import { CardInformation } from './CardInformation';
import { LeashContent } from 'leash/utilities/localStorage/leashContentStorage';
import date from 'utilities/dates/date';
import { isEmpty } from 'lodash';
import { CardGridLinkStyled, CardGridStyled } from './Card.styles';
import { CardInformationEmail } from './CardInformationEmail';

type Props = {
  person: Customer | Breeder;
  leadId?: number;
  titlePrefix: 'Contact' | 'Breeder';
  className?: string;
  deliveryAddress?: Address;
};

function getName(titlePrefix: 'Contact' | 'Breeder', person: Customer | Breeder) {
  if (titlePrefix === 'Contact') {
    const customer = person as Customer;
    return `${customer.firstName} ${customer.lastName}`;
  }

  return (person as Breeder).name;
}

function getAirportsList(airports: Airport[]) {
  const findAirportAbbreviation = (airport: Airport) => {
    if (!airport?.stateId) return null;

    return LeashContent.states.find((state: State) => state?.id === airport.stateId).abbr;
  };

  const airportCodeAndName = (airport: Airport) =>
    `${airport?.code ?? '--'}, ${airport?.name ?? '--'}`;

  return airports
    .map((a: Airport, i: number) => {
      const airportAbbreviation = findAirportAbbreviation(a);
      const listNumber = i + 1;
      if (!airportAbbreviation) {
        return `${listNumber}. ${airportCodeAndName(a)}`;
      } else {
        return `${listNumber}. ${airportCodeAndName(a)}, ${airportAbbreviation}`;
      }
    })
    .join(' ');
}

function geTitle(
  titlePrefix: 'Contact' | 'Breeder',
  leadId: number | undefined,
  person: Customer | Breeder,
) {
  const url =
    titlePrefix === 'Contact'
      ? `https://www.buypuppiesdirect.com/leadtracker/contact/edit/?id=${leadId}`
      : `https://www.buypuppiesdirect.com/leadtracker/breeder/edit/?id=${person.id}`;

  return (
    <>
      <a href={url} target="_blank" rel="noreferrer">
        Open in LeadTracker
      </a>
    </>
  );
}

export const PersonCard = ({
  person,
  className,
  titlePrefix,
  leadId,
  deliveryAddress,
}: Props): ReactElement => {
  const address = deliveryAddress?.address
    ? `${deliveryAddress.address}, ${deliveryAddress.address2}, ${deliveryAddress.city}, ${deliveryAddress.state}, ${deliveryAddress.postalCode}`
    : person.address
    ? `${person.address}, ${person.city}, ${person.stateAbbr}, ${person.postalCode}`
    : 'N/A';

  const name = getName(titlePrefix, person);

  return (
    <Card className={className}>
      <CardGridStyled>
        <CardInformation label="Name" value={name} />
        <CardInformation label="Address" value={address} />
        <CardInformationEmail label="Email" value={person.email} />
        <CardInformation label="Phone" value={person.phone} />
        {titlePrefix === 'Breeder' && (
          <CardInformation
            label="Breeder Tier"
            value={(person as Breeder)?.breederTier ?? ''}
          />
        )}
        {!isEmpty(person?.blackoutDates) && person.blackoutDates && (
          <CardInformation
            label="Blackout Dates"
            value={person.blackoutDates
              .map((d: BlackoutDate) => date.toUSLocale(d.blackoutDate))
              .join(', ')}
          />
        )}
        {!isEmpty(person?.airports) && person.airports && (
          <CardInformation label="Airports" value={getAirportsList(person.airports)} />
        )}
      </CardGridStyled>
      <CardGridLinkStyled>{geTitle(titlePrefix, leadId, person)}</CardGridLinkStyled>
    </Card>
  );
};
